import React, { useState, useEffect } from 'react';
import {
  FileText,
  Globe,
  Loader2,
  Eye,
  ChevronsLeft,
  ChevronsRight,
  X,
  CircleX,
} from 'lucide-react';
import { toastSuccess, showToast } from '../molecules/toast-helpers';
import { Button } from '../atoms/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../atoms/dropdown-menu';
import Icon from '../molecules/Icon';
import { Input } from '../atoms/input';
import { cn } from '@ideoz/ui';
import { PrototyperLoadingAnimation } from '..';

export interface PrototyperPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onDesignInsightsClick?: () => void;
  iframeSrc?: string;
  isLoading?: boolean;
  title?: string;
  children?: React.ReactNode;
  isWeb?: boolean;
  isReadyToCreate?: boolean;
  shouldShowBanner: () => boolean;
  onPublish?: (slug: string) => void;
  prototyperDemoLinkId?: number;
  prototyperSlug?: string | null;
  userName?: string;
  platformLink?: string;
  onToggleConversationPanel?: () => void;
  isConversationPanelCollapsed?: boolean;
}

export const PrototyperPanel: React.FC<PrototyperPanelProps> = ({
  isOpen,
  onClose,
  onDesignInsightsClick,
  iframeSrc,
  isLoading = false,
  title = 'Prototype View',
  children,
  isWeb,
  isReadyToCreate,
  shouldShowBanner,
  onPublish,
  prototyperDemoLinkId,
  prototyperSlug,
  userName,
  platformLink = 'https://ideoz.ai/',
  onToggleConversationPanel,
  isConversationPanelCollapsed = false,
}) => {
  // Local state for generating slug
  const [generatedSlug, setGeneratedSlug] = useState<string>('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [closePrototyperLoadingAnimation, setClosePrototyperLoadingAnimation] = useState(false);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  // Generate slug from username when component mounts or when data changes
  useEffect(() => {
    if (prototyperSlug) {
      // Use existing slug if available
      setGeneratedSlug(prototyperSlug);
    } else if (userName && prototyperDemoLinkId) {
      // Generate new slug: username/id-randomDigits
      const randomDigits = Math.floor(1000 + Math.random() * 9000);
      const cleanUserName = userName.toLowerCase().replace(/\s+/g, '');
      setGeneratedSlug(`${cleanUserName}/${prototyperDemoLinkId}${randomDigits}`);
    }
  }, [prototyperSlug, userName, prototyperDemoLinkId]);

  // Reset iframe loading state when iframeSrc changes
  useEffect(() => {
    if (iframeSrc) {
      setIsIframeLoading(true);
    }
  }, [iframeSrc]);

  // Handle iframe load event
  const handleIframeLoad = () => {
    // Add a small delay to ensure content is rendered (prevents black screen flash)
    // The onLoad event fires when iframe loads, but content may still be rendering
    setTimeout(() => {
      setIsIframeLoading(false);
      setClosePrototyperLoadingAnimation(false);
    }, 700); // 600ms delay to allow content to render
  };

  // Handle iframe error event
  const handleIframeError = () => {
    setIsIframeLoading(false);
    setClosePrototyperLoadingAnimation(false);
  };

  const handlePublish = async () => {
    if (!onPublish) return;

    setIsPublishing(true);
    try {
      if (prototyperSlug) {
        // Unpublish: send null to clear the slug
        await onPublish(null as any);
        toastSuccess('Prototype unpublished successfully');
      } else {
        // Publish: send the generated slug
        if (!generatedSlug) return;
        await onPublish(generatedSlug);
        // Use showToast for custom link support
        showToast({
          message: 'Prototype published successfully on',
          link: fullPrototypeUrl,
          linkText: fullPrototypeUrl,
          severity: 'success',
        });
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(fullPrototypeUrl);
    toastSuccess('Link copied to clipboard');
    // Close the dropdown
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
  };

  const fullPrototypeUrl = `${platformLink}p/${generatedSlug}`;

  if (!isOpen) return null;

  return (
    <div className="h-full flex flex-col bg-gray-50 relative">
      <div
        className={`p-4 flex flex-col ${
          shouldShowBanner() && !isReadyToCreate
            ? isWeb
              ? 'mt-[theme(spacing.header-has-banner)]'
              : 'mt-[theme(spacing.plugin-header-has-banner)]'
            : ''
        } ${isWeb ? 'h-[calc(100vh-theme(spacing.header))] mt-[theme(spacing.header)]' : 'h-[calc(100vh-theme(spacing.plugin-header))] mt-[theme(spacing.plugin-header)]'}`}
      >
        <div className={`flex items-center justify-between mb-4 gap-3`}>
          {/* Toggle Conversation Panel Button */}
          {onToggleConversationPanel && (
            <Button
              onClick={onToggleConversationPanel}
              variant="ghost"
              size="sm"
              className="!p-1 h-auto border border-gray-300 gap-0.5"
              aria-label={
                isConversationPanelCollapsed ? 'Show conversation panel' : 'Hide conversation panel'
              }
            >
              <Icon
                icon={isConversationPanelCollapsed ? ChevronsRight : ChevronsLeft}
                size={24}
                className={cn(
                  'rounded-full p-1',
                  isConversationPanelCollapsed
                    ? 'bg-primary-900 stroke-white'
                    : 'bg-white stroke-primary-900'
                )}
              />
              <Icon
                icon={Eye}
                size={24}
                className={cn(
                  'rounded-full p-1',
                  isConversationPanelCollapsed
                    ? 'bg-white stroke-primary-900'
                    : 'bg-primary-900 stroke-white'
                )}
              />
            </Button>
          )}

          <div className="flex items-center gap-3 ml-auto">
            {!isLoading && onDesignInsightsClick && (
              <Button onClick={onDesignInsightsClick} variant="secondary" size="sm">
                View Insights
              </Button>
            )}

            {/* Publish Dropdown Menu */}
            {!isLoading && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className="relative">
                    <Button variant="primary" size="sm">
                      Publish
                    </Button>
                    {/* Success indicator when prototype is ready */}
                    {iframeSrc && !isIframeLoading && (
                      <span className="absolute -top-1 right-2 flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#17B26A] border border-[#FAFAFA]"></span>
                      </span>
                    )}
                  </div>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  side="bottom"
                  align="end"
                  className="w-[400px] p-5 text-sm bg-primary-900 rounded-xl"
                >
                  {/* Publish on Ideoz.ai Section */}
                  <div className="mb-4">
                    <div className="flex items-start gap-3 text-white mb-3">
                      <Icon icon={Globe} color="#fff" className="mt-0.5 flex-shrink-0" size={24} />
                      <div>
                        <h3 className="font-semibold mb-1">Publish on Ideoz.ai</h3>
                        <p className="text-[#D5D7DA] text-xs">
                          Share your prototype with others by publishing to the web.
                        </p>
                      </div>
                    </div>
                  </div>

                  <DropdownMenuSeparator className="bg-[#414651] my-3" />

                  {/* URL Display */}
                  <div className="mb-4">
                    <div className="flex items-start gap-2">
                      <Icon
                        icon={FileText}
                        color="#D5D7DA"
                        size={22}
                        className="mt-0.5 flex-shrink-0"
                      />
                      <div className="flex gap-8  items-start">
                        <span className="text-[#D5D7DA] text-xs leading-6">URL</span>
                        <span className="text-white text-xs leading-6 break-all">
                          {fullPrototypeUrl.replace(/^(https?:\/\/)/, '')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <DropdownMenuSeparator className="bg-[#414651] my-3" />

                  {/* Action Buttons */}
                  {prototyperSlug ? (
                    // Show Copy Link and Unpublish when already published
                    <div className="space-y-2">
                      <Button
                        onClick={handleCopyLink}
                        size={'sm'}
                        className="w-full bg-white text-primary-900 font-semibold py-2.5 px-4 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        Copy link
                      </Button>
                      <Button
                        onClick={handlePublish}
                        disabled={isPublishing}
                        size={'sm'}
                        variant="ghost"
                        className="w-full text-white font-semibold py-2.5 px-4 rounded-lg hover:bg-[#414651] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isPublishing ? (
                          <>
                            <Icon icon={Loader2} className="animate-spin mr-2" size={16} />
                            Unpublishing...
                          </>
                        ) : (
                          'Unpublish'
                        )}
                      </Button>
                    </div>
                  ) : (
                    // Show Publish button when not published
                    <Button
                      onClick={handlePublish}
                      disabled={isPublishing || !generatedSlug}
                      size={'sm'}
                      className="w-full bg-white text-primary-900 font-semibold py-2.5 px-4 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isPublishing ? (
                        <>
                          <Icon icon={Loader2} className="animate-spin mr-2" size={16} />
                          Publishing...
                        </>
                      ) : (
                        'Publish'
                      )}
                    </Button>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Button onClick={onClose} aria-label="Close prototype view" variant="ghost">
              <Icon icon={CircleX} size={24} />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto h-full relative">
          {iframeSrc ? (
            <>
              <iframe
                className={`w-full h-full border-0 ${isIframeLoading ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}`}
                src={iframeSrc}
                title={title || 'Prototype Preview'}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={handleIframeLoad}
                onError={handleIframeError}
              />
              {/* Show loading overlay while iframe is loading */}
              {isIframeLoading && <PrototyperLoadingAnimation />}
              {/* Show updating popup while isLoading prop is true (and iframe is loaded) */}
              {isLoading && !isIframeLoading && !closePrototyperLoadingAnimation && (
                <div className="absolute bottom-4 right-4 bg-gray-100 rounded-2xl px-6 py-4 shadow-xl z-20 w-auto h-[280px]">
                  <PrototyperLoadingAnimation />
                </div>
              )}
            </>
          ) : isLoading || isIframeLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <PrototyperLoadingAnimation />
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
};

export default PrototyperPanel;
